import logger from "../common/Logger";
import Auth0Lock from "auth0-lock";
import CoreService from "./CoreService";
import StorageService from "./StorageService";
import ConfigService, {ConfigConstants} from "./ConfigService";
import SiteService from "./SiteService";

const userSessionKey = 'userSession';
const postLoginUrlKey = 'postLoginUrl';
const domain = 'gatehill.eu.auth0.com';

class UserSession {
    /**
     * @param idToken {String}
     * @param email {String}
     */
    constructor(idToken, email) {
        this.idToken = idToken;
        this.email = email;
    }
}

class AuthService {
    /**
     * Configure the lock component.
     *
     * @param mode {string} - one of 'login', 'signUp' or 'forgotPassword'
     * @param containerId {string}
     * @param callback {Function}
     * @private
     */
    _configure = (mode, containerId, callback) => {
        ConfigService.getItem(ConfigConstants.auth0ClientId, (clientId) => {

            // Configure Auth0
            const lock = new Auth0Lock(clientId, domain, {
                auth: {
                    redirectUrl: `${CoreService.getBasePath()}/login`,
                    responseType: 'token id_token',
                    params: {scope: 'openid email'}
                },
                container: containerId,
                allowLogin: (mode === 'login'),
                allowSignUp: (mode === 'signUp'),
                initialScreen: mode,
                theme: {
                    logo: `${CoreService.getBasePath()}/assets/img/auth0_lock_logo.png`
                },
                languageDictionary: {
                    title: SiteService.getSiteName()
                },
            });

            // Add callback for lock `authenticated` event
            lock.on('authenticated', this._doAuthentication);
            lock.on('authorization_error', this._authError);
            lock.on('unrecoverable_error', this._authError);

            callback(lock);
        });
    };

    _authError = (error) => {
        logger.error('Authentication error', error);
    };

    _doAuthentication = (authResult) => {
        logger.debug('Received authenticated event');

        StorageService.setSessionItem(userSessionKey, new UserSession(
            authResult.idToken,
            (authResult.idTokenPayload ? authResult.idTokenPayload.email : null)
        ));

        // check for destination
        const postLoginUrl = StorageService.getSessionItem(postLoginUrlKey);
        StorageService.removeSessionItem(postLoginUrlKey);

        if (postLoginUrl && !postLoginUrl.startsWith('/login')) {
            CoreService.navigateTo(postLoginUrl);
        } else {
            CoreService.navigateToHome();
        }
    };

    login = (containerId) => {
        this._configure('login', containerId, (lock) => {
            lock.show();
        });
    };

    register = (containerId) => {
        this._configure('signUp', containerId, (lock) => {
            lock.show();
        });
    };

    forgotPassword = (containerId) => {
        this._configure('forgotPassword', containerId, (lock) => {
            lock.show();
        });
    };

    loggedIn = () => {
        // Checks if there is a saved token and it's still valid
        return !!this.getToken();
    };

    getToken = () => {
        const user = this.getUserSession();
        return (user ? user.idToken : null);
    };

    logout = () => {
        // Clear user token and profile data from local storage
        StorageService.removeSessionItem(userSessionKey);
        StorageService.removeSessionItem(postLoginUrlKey);
        CoreService.navigateToHome();
    };

    checkAuthenticatedSession = (callback) => {
        const loggedIn = this.loggedIn();
        logger.debug(`Session valid: ${loggedIn}`);
        callback(loggedIn);
    };

    /**
     * Set the post-login URL.
     *
     * @param postLoginUrl {String}
     */
    setPostLoginUrl = (postLoginUrl) => {
        logger.debug(`Set post login URL: ${postLoginUrl}`);
        StorageService.setSessionItem(postLoginUrlKey, postLoginUrl);
    };

    /**
     * Fetch the user session.
     *
     * @returns {UserSession} the session - may be `null`
     */
    getUserSession = () => {
        return StorageService.getSessionItem(userSessionKey);
    };
}

export default new AuthService();



// WEBPACK FOOTER //
// ./src/modules/service/AuthService.js